import { useEffect, useId, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { Question } from '../../config/types';
import { useFormData } from '../../hooks/useFormData';
import {
  chainValidations,
  checkAllFieldsHaveValues,
  groupValidation,
  objectToString,
  stringToObject,
  sumRow,
} from '../../utils/form-utils';
import GradeInputColumn from './DateInputField/GradeInputColumn';
import { dateIsEmpty } from './DateInputField/utils';
import Details from './Details';
import ErrorMessage from './ErrorMessage';
interface GradeInputProps {
  name: string;
  defaultValue?: string;
  disabled?: boolean;
  anchorId?: string;
  onChange?(value: string): void;
}

export const getColumns = () => ['White', 'EthnicMinority', 'Undisclosed', 'Total'];
export const getRows = () => ['scs2', 'scs1', 'grade6', 'grade7', 'seo', 'heo', 'eo', 'ao', 'aa'];
export const getKeys = () => {
  const columns = getColumns();
  const rows = getRows();
  return rows.flatMap((row) => columns.map((column) => `${row}${column}`));
};

export function GradeInputWrapperColumn({
  defaultValue,
  disabled = false,
  anchorId = useId(),
  onChange,
}: GradeInputProps) {
  const generateInputId = (suffix: string) => `${anchorId}-${suffix}`;

  const initialValues = useMemo(() => stringToObject(defaultValue), [defaultValue]);

  const [values, setValues] = useState(initialValues);

  const triggerChange = (updatedValues: typeof values) => {
    if (onChange) {
      const valueString = objectToString(updatedValues);
      if (dateIsEmpty(valueString)) {
        onChange('');
      } else {
        onChange(valueString);
      }
    }
  };

  // Type the field argument to only accept valid keys of 'values'
  const handleValueChange = (field: keyof typeof values) => (newValue: string) => {
    const updatedValues = { ...values, [field]: newValue };
    const rows = ['scs2', 'scs1', 'grade6', 'grade7', 'seo', 'heo', 'eo', 'ao', 'aa'];
    const rowTotals = rows.reduce(
      (acc, row) => {
        const total = sumRow(row, updatedValues, 'Total');
        acc[`${row}Total`] = total && total !== 0 ? total.toString() : '';
        return acc;
      },
      {} as Record<string, string>,
    );
    // Add totals to the updatedValues
    const updatedValuesWithTotals = { ...updatedValues, ...rowTotals };
    setValues(updatedValuesWithTotals);
    triggerChange(updatedValuesWithTotals);
  };

  const gradeRows = useMemo(
    () => [
      {
        grade: 'SCS 2',
        fields: ['scs2White', 'scs2EthnicMinority', 'scs2Undisclosed', 'scs2Total'],
      },
      {
        grade: 'SCS 1',
        fields: ['scs1White', 'scs1EthnicMinority', 'scs1Undisclosed', 'scs1Total'],
      },
      {
        grade: 'Grade 6',
        fields: ['grade6White', 'grade6EthnicMinority', 'grade6Undisclosed', 'grade6Total'],
      },
      {
        grade: 'Grade 7',
        fields: ['grade7White', 'grade7EthnicMinority', 'grade7Undisclosed', 'grade7Total'],
      },
      { grade: 'SEO', fields: ['seoWhite', 'seoEthnicMinority', 'seoUndisclosed', 'seoTotal'] },
      { grade: 'HEO', fields: ['heoWhite', 'heoEthnicMinority', 'heoUndisclosed', 'heoTotal'] },
      { grade: 'EO', fields: ['eoWhite', 'eoEthnicMinority', 'eoUndisclosed', 'eoTotal'] },
      { grade: 'AO', fields: ['aoWhite', 'aoEthnicMinority', 'aoUndisclosed', 'aoTotal'] },
      { grade: 'AA', fields: ['aaWhite', 'aaEthnicMinority', 'aaUndisclosed', 'aaTotal'] },
    ],
    [],
  );

  return (
    <div>
      <table className="govuk-table">
        <thead className="govuk-table__head">
          <tr className="govuk-table__row">
            <th scope="col" className="govuk-table__header">
              Grade
            </th>
            <th scope="col" className="govuk-table__header">
              White
            </th>
            <th scope="col" className="govuk-table__header">
              Ethnic minority
            </th>
            <th scope="col" className="govuk-table__header">
              Undisclosed
            </th>
            <th scope="col" className="govuk-table__header">
              Grade totals
            </th>
          </tr>
        </thead>
        <tbody className="govuk-table__body">
          {gradeRows.map(({ grade, fields }) => (
            <tr key={grade} className="govuk-table__row">
              <th scope="row" className="govuk-table__header">
                {grade}
              </th>
              {fields.map((field) => (
                <td key={field} className="govuk-table__cell">
                  <GradeInputColumn
                    testId={`${field}-${grade.toLowerCase().replace(' ', '-')}`}
                    disabled={field.endsWith('Total') ? true : disabled}
                    anchorId={generateInputId(field)}
                    onChange={handleValueChange(field as keyof typeof values)} // Cast field to a valid key
                    value={values[field as keyof typeof values]} // Cast field to a valid key
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface GradeInputFieldProps {
  question: Question;
  disabled?: boolean;
  anchorId: string;
  detailsHeading?: string;
  detailsContent?: string;
}

export function EthnicityInput({
  question,
  disabled,
  anchorId,
  detailsHeading,
  detailsContent,
}: GradeInputFieldProps) {
  const { register, getFieldState, getValues, setValue } = useFormContext();
  const { error } = getFieldState(question.name);

  useEffect(() => {
    if (getValues(question.name) === undefined) {
      setValue(question.name, '');
    }
  }, [getValues, question.name, setValue]);

  const { data } = useFormData();
  const { onChange, name } = register(question.name, {
    validate: (value: string) =>
      chainValidations([
        checkAllFieldsHaveValues({
          value: stringToObject(value),
          keys: getKeys().filter((key) => !key.endsWith('Total')),
        }),
        groupValidation({
          value: stringToObject(value),
          answers: data?.answers,
          question,
          sumColumns: [
            'scs2Total',
            'scs1Total',
            'grade6Total',
            'grade7Total',
            'seoTotal',
            'heoTotal',
            'eoTotal',
            'aoTotal',
            'aaTotal',
          ],
        }),
      ]),
  });
  const handleChange = (dateChange: string) => {
    onChange({ target: { name, value: dateChange } });
  };

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}
      <div className="govuk-date-input">
        <GradeInputWrapperColumn
          name={question.name}
          disabled={disabled}
          anchorId={anchorId}
          onChange={handleChange}
          defaultValue={getValues(question.name)}
        />
      </div>
    </>
  );
}
